import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  useCreateResendConnection,
  useUpdateResendConnection,
} from "@/hooks/use-resend-connections";

import {
  defaultResendConnectionValues,
  resendConnectionSchema,
  type ResendConnectionFormValues,
} from "./resend-connection-schema";

import type { ResendConnection } from "@artisancode/api-types";

export function useResendConnectionForm(
  open: boolean,
  editing: ResendConnection | null,
  onSuccess: () => void,
) {
  const { mutate: create, isPending: isCreating } = useCreateResendConnection();
  const { mutate: update, isPending: isUpdating } = useUpdateResendConnection();

  const form = useForm<ResendConnectionFormValues>({
    resolver: zodResolver(resendConnectionSchema),
    defaultValues: defaultResendConnectionValues,
  });

  useEffect(() => {
    if (!open) return;
    if (!editing) {
      form.reset(defaultResendConnectionValues);
      return;
    }
    form.reset({
      name: editing.name,
      fromAddress: editing.fromAddress,
      apiKey: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  function onSubmit(values: ResendConnectionFormValues) {
    const payload = {
      name: values.name,
      fromAddress: values.fromAddress.trim(),
      apiKey: values.apiKey?.trim() ? values.apiKey.trim() : undefined,
    };

    if (editing) {
      update(
        { id: editing.id, ...payload },
        {
          onSuccess: () => {
            toast.success("Koneksi Resend berhasil diperbarui.");
            onSuccess();
          },
        },
      );
    } else {
      create(
        { ...payload, apiKey: payload.apiKey ?? "" },
        {
          onSuccess: () => {
            toast.success("Koneksi Resend berhasil ditambahkan.");
            onSuccess();
          },
        },
      );
    }
  }

  return {
    form,
    isSubmitting: isCreating || isUpdating,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
